import {
  BlurEvent
} from '@progress/kendo-react-dropdowns/dist/es/common/events';
import { NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { MultiSelectChangeEvent } from '@progress/kendo-react-dropdowns';

export enum FORM_CONTROL_VALIDATE_TYPES {
  email = 'email',
  noPastTime = 'no past time',
  noPastTomorrow = 'no past tomorrow',
  onlyChars = 'only chars',
  sentence = 'sentence',
  maxTweetLength = 'max tweet length',
  colorRequired = 'colorRequired',
  languageRequired = 'languageRequired',
  path = 'path',
  url = 'url',
  localesPath = 'localesPath',
  noAmpersandAndSlash = 'noAmpersandAndSlash',
  maxCharactersLength = 'maxCharactersLength',
  maxTextLength = 'maxTextLength'
}

export type ABSTRACT_BASE_FORM_PROPS = {
  moduleId?: string
  form?: FORM_CONTROL,
  disableEnableActionName?: string
}

export type FORM_CONTROL = {
  /**
   * Generate initial state data
   */
  generateInitialData: (customFields?: any) => Record<string, FORM_CONTROL_STATE_DATA>
  /**
   * Returns raw form data e.g. { [key]: value }
   */
  getData: (keys?: string | string[]) => { [key: string]: any }
  /**
   * Return fields all configuration object
   */
  getAllConfigField: () => { [key: string]:FORM_CONTROL_STATE_DATA }
  /**
   * Return fields configuration object
   */
  getFields: () => FORM_GET_FIELDS
  /**
   * On blur delete the value of an invalid field if it's not required
   * @param {BlurEvent | any } e
   * @param {Function} afterChangeHandler Form field Function
   */
  handleBlur: (e: BlurEvent<any> | any, afterChangeHandler?: Function) => void
  /**
   * Update the form data when input's value changes
   * @param {NumericTextBoxChangeEvent | DatePickerChangeEvent | MultiSelectChangeEvent | any} e
   * @param {Function} afterChangeHandler Form field Function
   * @param ignoreChangeValue
   */
  handleChange: (e: NumericTextBoxChangeEvent | DatePickerChangeEvent | MultiSelectChangeEvent | any, afterChangeHandler?: Function, ignoreChangeValue?: boolean) => void
  /**
   * Ignore fields
   * @param {string[]} ignoredFields
   */
  ignoreFields: (ignoredFields: string[]) => void
  /**
   * Is valid
   */
  isValid: () => boolean,
  /**
   * Returns are all required fields valid. Use for initial validation.
   * If some of the fields are required={true}, valid={true} and value={''},
   * isAllFieldValid method will return false.
   */
  isAllFieldValid: () => boolean
  /**
   * Remove all fields that are not in init configuration,
   * and keeps values of once that were in init configuration.
   * @param {boolean} removeIgnoredFields
   */
  removeAdditionallyAddedFields: (removeIgnoredFields?: boolean) => void
  /**
   * Restart isAllValid value in state
   * @param {Function} afterResetHandler
   */
  reset: (afterResetHandler?: Function) => void
  /**
   * Reset form to initial state
   * Remove all dynamic rules and additional fields
   */
  resetToInitialState: () => void
  /**
   * Clear all
   * @param {Function} afterClearAllHandler
   * @param {any} defaultData
   * @param {any} customFields
   */
  clearAll: (afterClearAllHandler?: Function, defaultData?: any, customFields?: any) => void
  /**
   * Validate each field's rules and update global validity state
   */
  runFullValidation: (staticFields?: string[]) => boolean
  /**
   * Dynamically set fields if config cannot be provided beforehand (e.g. comes from an API)
   * @param {any} fields
   * @param {Function} afterClearAllHandler
   */
  setFields: (fields: any, afterClearAllHandler?: Function) => void
  /**
   * Update each field's values
   * @param {Record<string, FORM_CONTROL_STATE_DATA>} data
   * @param {any} dynamicRules
   * @param {Function} afterChangeHandler
   */
  setData: (data: { [key: string]: any }, dynamicRules?: any, afterChangeHandler?: Function) => void;
  /**
   * Sets dynamic rules
   * @param {Object} dynamicRules
   */
  setDynamicRules: (dynamicRules: {}) => void
  /**
   * Validate a single field
   */
  validateField: (name: string, value: any, dynamicRules?: any) => FORM_CONTROL_STATE_DATA
}

export type FORM_CONTROL_STATE = {
  data: Record<string, FORM_CONTROL_STATE_DATA>,
  dynamicRules: any,
  valid: boolean,
  ignoredFields: string[],
  isAllValid: boolean
};

export type FORM_CONTROL_STATE_DATA = {
  'aria-describedby'?: string,
  ariaDescribedBy?: string,
  'aria-invalid'?: boolean,
  'data-commonnamespace'?: boolean,
  disabled?: boolean,
  'data-error'?: string,
  hidden?: boolean,
  required?: boolean,
  rules?: (string | Function)[],
  value?: any,
  valid?: boolean,
  hasDynamicRule?: boolean,
  additionalRulesConfig?: {}
  errorTxt?: string
};

export type FORM_GET_FIELD = {
  'aria-describedby'?: string,
  ariaDescribedBy?: string,
  'aria-invalid'?: boolean,
  'data-commonnamespace'?: boolean,
  disabled?: boolean,
  hidden?: boolean,
  required?: boolean,
  value?: any,
  valid?: boolean
  errorTxt?: string
}

export type FORM_GET_FIELDS = {
  [key: string]: FORM_GET_FIELD
}

export type FORM_CONTROL_CONFIG = Record<string, FORM_CONTROL_STATE_DATA>;
